"use strict";
globalThis.CVMeasure = (() => {
  const { $, hide, show } = Utils;
  const state = State.cvMeasure;
  const TOUCH_TARGET = 60;
  const MIN_SHAPE_SIZE = 30;
  const getApiUrl = () => globalThis.FRAME_MEASUREMENT_URL || "";
  let homographyMatrix = null;
  let dragCleanup = null;
  let multiFrameMode = false;
  let detectedFrames = [];
  let selectedFrameIndex = 0;
  const buildContent = () => {
    const markerUrl = `${getApiUrl()}/marker/aruco.pdf`;
    return `
            <div class="p-4">
                <!-- Mode toggle -->
                <div id="cv-mode-toggle" class="flex gap-2 mb-4 p-1 bg-neutral-100 rounded-xl">
                    <button type="button" data-mode="single" class="flex-1 py-2.5 px-3 text-sm font-semibold rounded-lg transition-all bg-white shadow text-neutral-900">
                        Enkel kozijn
                    </button>
                    <button type="button" data-mode="multi" class="flex-1 py-2.5 px-3 text-sm font-semibold rounded-lg transition-all text-neutral-500">
                        Hele gevel
                    </button>
                </div>
                
                <!-- Instructions (single mode) -->
                <div id="cv-single-instructions" class="mb-4 p-3 bg-blue-50 rounded-lg">
                    <p class="text-sm text-neutral-700 mb-2">
                        <strong>Voor de beste meting:</strong> Plak de marker op het kozijn.
                    </p>
                    <a href="${markerUrl}" target="_blank" class="inline-flex items-center gap-1 text-[#D40032] font-semibold text-sm hover:underline">
                        <i data-lucide="download" class="w-4 h-4"></i>
                        Download marker (PDF)
                    </a>
                    <p class="text-xs text-neutral-500 mt-2">
                        Geen marker? Leg een A4 papier tegen het kozijn, of maak een foto van een metselwerk gevel.
                    </p>
                </div>
                
                <!-- Instructions (multi mode) -->
                <div id="cv-multi-instructions" class="hidden mb-4 p-3 bg-amber-50 rounded-lg">
                    <p class="text-sm text-neutral-700">
                        <strong>Maak een foto van de hele gevel.</strong> Alle ramen en deuren worden automatisch gedetecteerd via bakstenen telling.
                    </p>
                    <p class="text-xs text-neutral-500 mt-2">
                        <i data-lucide="info" class="w-3.5 h-3.5 inline-block mr-1"></i>
                        Werkt het beste bij metselwerk gevels met zichtbare bakstenen.
                    </p>
                </div>
                
                <!-- Multi-frame results -->
                <div id="cv-multi-results" class="hidden mb-4">
                    <div class="flex items-center justify-between mb-3">
                        <h3 class="font-bold text-neutral-900">
                            <span id="cv-frame-count">0</span> kozijnen gevonden
                        </h3>
                        <button type="button" id="cv-select-all-btn" class="text-sm text-[#D40032] font-semibold">
                            Selecteer alle
                        </button>
                    </div>
                    
                    <!-- Frame preview image with overlays -->
                    <div id="cv-multi-preview" class="relative bg-neutral-900 rounded-xl overflow-hidden mb-3">
                        <img id="cv-multi-img" class="w-full h-auto block select-none" draggable="false" />
                        <svg id="cv-multi-overlay" class="absolute inset-0 w-full h-full pointer-events-none"></svg>
                        <div id="cv-multi-handles" class="absolute inset-0"></div>
                    </div>
                    
                    <!-- Frame list -->
                    <div id="cv-frame-list" class="space-y-2 max-h-48 overflow-y-auto"></div>
                </div>
                
                <!-- Single frame preview area -->
                <div id="cv-preview" class="hidden relative bg-neutral-900 rounded-xl overflow-hidden mb-4 shadow-lg touch-none">
                    <div id="cv-preview-container" class="relative">
                        <img id="cv-preview-img" class="w-full h-auto block select-none" crossorigin="anonymous" draggable="false" />
                        
                        <!-- SVG overlay for corners -->
                        <svg id="cv-overlay" class="absolute inset-0 w-full h-full pointer-events-none">
                            <defs>
                                <mask id="cv-mask">
                                    <rect width="100%" height="100%" fill="white"/>
                                    <polygon id="cv-mask-poly" fill="black"/>
                                </mask>
                            </defs>
                            <rect width="100%" height="100%" fill="rgba(0,0,0,0.4)" mask="url(#cv-mask)"/>
                            <polygon id="cv-outline" fill="none" stroke="#D40032" stroke-width="2"/>
                        </svg>
                        
                        <!-- Corner handles - inline, no fullscreen -->
                        <div id="cv-corners" class="absolute inset-0 pointer-events-auto"></div>
                    </div>
                    
                    <!-- Edit hint -->
                    <div id="cv-edit-hint" class="absolute top-2 inset-x-0 text-center pointer-events-none">
                        <span class="bg-black/60 text-white text-xs px-3 py-1 rounded-full">
                            Sleep hoeken om aan te passen
                        </span>
                    </div>
                    
                    <!-- Result overlay -->
                    <div class="absolute inset-x-0 bottom-0 bg-linear-to-t from-black/90 to-transparent p-4 pt-8 pointer-events-none">
                        <div class="flex items-center justify-between">
                            <div>
                                <div id="cv-detection-info" class="text-xs text-green-400 mb-1 flex items-center gap-1">
                                    <i data-lucide="check-circle" class="w-3.5 h-3.5"></i>
                                    <span>Kozijn gedetecteerd</span>
                                </div>
                                <div id="cv-result-dims" class="text-2xl font-bold text-white"></div>
                            </div>
                            <button type="button" id="cv-reset-btn" class="pointer-events-auto w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white" title="Reset hoeken">
                                <i data-lucide="rotate-ccw" class="w-5 h-5"></i>
                            </button>
                        </div>
                    </div>
                </div>
                
                <!-- Action buttons (single mode) -->
                <div id="cv-actions" class="hidden space-y-2 mb-4">
                    <button type="button" id="cv-use-btn" class="w-full py-4 bg-[#D40032] text-white font-bold rounded-xl hover:bg-[#b0002a] active:scale-[0.98] transition-all flex items-center justify-center gap-2 shadow-lg">
                        <i data-lucide="check" class="w-5 h-5"></i>
                        Gebruik deze afmetingen
                    </button>
                </div>
                
                <!-- Action buttons (multi mode) -->
                <div id="cv-multi-actions" class="hidden space-y-2 mb-4">
                    <button type="button" id="cv-add-selected-btn" class="w-full py-4 bg-[#D40032] text-white font-bold rounded-xl hover:bg-[#b0002a] active:scale-[0.98] transition-all flex items-center justify-center gap-2 shadow-lg">
                        <i data-lucide="plus" class="w-5 h-5"></i>
                        <span>Voeg geselecteerde toe</span>
                    </button>
                    <button type="button" id="cv-new-photo-btn" class="w-full py-3 bg-neutral-100 text-neutral-700 font-semibold rounded-xl hover:bg-neutral-200 active:scale-[0.98] transition-all">
                        Nieuwe foto maken
                    </button>
                </div>

                <!-- Upload buttons -->
                <div id="cv-buttons" class="grid grid-cols-2 gap-3">
                    <label class="cursor-pointer block">
                        <div class="w-full min-h-14 py-3 bg-[#D40032] text-white font-bold rounded-xl hover:bg-[#b0002a] active:scale-[0.98] transition-all flex items-center justify-center gap-2 shadow-lg">
                            <i data-lucide="camera" class="w-5 h-5"></i>
                            Maak foto
                        </div>
                        <input type="file" id="cv-camera" accept="image/*" capture="environment" class="hidden" />
                    </label>
                    <label class="cursor-pointer block">
                        <div class="w-full min-h-14 py-3 bg-white border-2 border-neutral-200 text-neutral-700 font-bold rounded-xl hover:border-[#D40032] active:scale-[0.98] transition-all flex items-center justify-center gap-2">
                            <i data-lucide="upload" class="w-5 h-5"></i>
                            Upload
                        </div>
                        <input type="file" id="cv-upload" accept="image/*" class="hidden" />
                    </label>
                </div>

                <!-- Loading state -->
                <div id="cv-loading" class="hidden text-center py-8">
                    <div class="inline-flex flex-col items-center gap-3">
                        <div class="relative w-16 h-16">
                            <div class="absolute inset-0 border-4 border-neutral-200 rounded-full"></div>
                            <div class="absolute inset-0 border-4 border-[#D40032] border-t-transparent rounded-full animate-spin"></div>
                            <i data-lucide="scan" class="absolute inset-0 m-auto w-6 h-6 text-[#D40032]"></i>
                        </div>
                        <div>
                            <p class="text-[#D40032] font-semibold">Foto analyseren...</p>
                            <p id="cv-loading-status" class="text-neutral-500 text-sm">Kozijn detecteren</p>
                        </div>
                    </div>
                </div>

                <!-- Error state -->
                <div id="cv-error" class="hidden text-center py-6 px-4 bg-red-50 rounded-xl border border-red-200">
                    <i data-lucide="alert-triangle" class="w-8 h-8 mx-auto mb-2 text-red-500"></i>
                    <p id="cv-error-msg" class="text-red-700 font-medium">Er ging iets mis</p>
                    <button type="button" id="cv-retry-btn" class="mt-3 px-4 py-2 bg-red-100 text-red-700 font-medium rounded-lg hover:bg-red-200 transition-colors text-sm">
                        Opnieuw proberen
                    </button>
                </div>
            </div>
        `;
  };
  const setMode = (mode) => {
    multiFrameMode = mode === "multi";
    const toggleBtns = document.querySelectorAll("#cv-mode-toggle button");
    toggleBtns.forEach((btn) => {
      const isActive = btn.dataset.mode === mode;
      btn.className = `flex-1 py-2.5 px-3 text-sm font-semibold rounded-lg transition-all ${isActive ? "bg-white shadow text-neutral-900" : "text-neutral-500"}`;
    });
    const singleInstr = $("#cv-single-instructions");
    const multiInstr = $("#cv-multi-instructions");
    if (multiFrameMode) {
      hide(singleInstr);
      show(multiInstr);
    } else {
      show(singleInstr);
      hide(multiInstr);
    }
    reset();
  };
  const initListeners = (onSuccess, onMultiSuccess) => {
    const content = $("#bottomsheet-content");
    if (!content) return;
    state.set("onSuccessCallback", onSuccess);
    state.set("onMultiSuccessCallback", onMultiSuccess);
    document.querySelectorAll("#cv-mode-toggle button").forEach((btn) => {
      btn.addEventListener("click", () => setMode(btn.dataset.mode));
    });
    $("#cv-camera")?.addEventListener("change", (e) => processImage(e.target.files[0]));
    $("#cv-upload")?.addEventListener("change", (e) => processImage(e.target.files[0]));
    $("#cv-retry-btn")?.addEventListener("click", reset);
    $("#cv-new-photo-btn")?.addEventListener("click", reset);
    $("#cv-reset-btn")?.addEventListener("click", resetCorners);
    $("#cv-use-btn")?.addEventListener("click", () => {
      const result = state.get("result");
      const callback = state.get("onSuccessCallback");
      if (result && callback) callback(result);
      Bottomsheet.close();
    });
    $("#cv-add-selected-btn")?.addEventListener("click", addSelectedFrames);
    $("#cv-select-all-btn")?.addEventListener("click", toggleSelectAll);
  };
  const toggleSelectAll = () => {
    const allSelected = detectedFrames.every((f) => f.selected);
    detectedFrames.forEach((f) => f.selected = !allSelected);
    updateFrameList();
    updateMultiOverlay();
    updateAddButtonState();
  };
  const addSelectedFrames = () => {
    const selected = detectedFrames.filter((f) => f.selected);
    const callback = state.get("onMultiSuccessCallback");
    if (selected.length > 0 && callback) {
      const results = selected.map((f) => ({
        width: f.dimensions.width,
        height: f.dimensions.height,
        confidence: f.confidence,
        method: "brick_count",
        label: f.label
      }));
      callback(results);
    }
    Bottomsheet.close();
  };
  const getAddButtonText = (count) => {
    if (count === 0) return "Selecteer kozijnen";
    const plural = count > 1 ? "en" : "";
    return `Voeg ${count} kozijn${plural} toe`;
  };
  const updateAddButtonState = () => {
    const btn = $("#cv-add-selected-btn");
    const selected = detectedFrames.filter((f) => f.selected);
    if (btn) {
      const span = btn.querySelector("span");
      if (span) {
        span.textContent = getAddButtonText(selected.length);
      }
      btn.disabled = selected.length === 0;
      btn.classList.toggle("opacity-50", selected.length === 0);
    }
  };
  const resetCorners = () => {
    const originalCorners = state.get("originalCorners");
    if (!originalCorners) return;
    const corners = originalCorners.map((c) => ({ ...c }));
    state.set("displayCorners", corners);
    updateOverlay(corners);
    updateHandles(corners);
    recalculateDimensions();
    if (navigator.vibrate) navigator.vibrate(15);
  };
  const DETECTION_METHODS = [
    { type: "aruco", label: "Marker zoeken..." },
    { type: "a4_paper", label: "A4 papier zoeken..." },
    { type: "brick_count", label: "Bakstenen analyseren..." }
  ];
  const tryDetectionMethod = async (apiUrl, file, method) => {
    const formData = new FormData();
    formData.append("image", file);
    formData.append("reference_type", method.type);
    const response = await fetch(`${apiUrl}/api/v1/measure`, {
      method: "POST",
      body: formData
    });
    if (!response.ok) return null;
    const data = await response.json();
    return data.status === "success" && data.physical_dimensions ? data : null;
  };
  const autoDetect = async (apiUrl, file, onProgress) => {
    for (const method of DETECTION_METHODS) {
      onProgress?.(method.label);
      try {
        const result = await tryDetectionMethod(apiUrl, file, method);
        if (result) return { result, method: method.type };
      } catch (err) {
        console.debug(`Detection with ${method.type} failed:`, err.message);
      }
    }
    return null;
  };
  const tryMultiDetection = async (apiUrl, file) => {
    const formData = new FormData();
    formData.append("image", file);
    formData.append("reference_type", "brick_count");
    formData.append("detection_target", "facade");
    formData.append("min_confidence", "0.3");
    const response = await fetch(`${apiUrl}/api/v1/measure-all`, {
      method: "POST",
      body: formData
    });
    if (!response.ok) return null;
    const data = await response.json();
    return data.status === "success" && data.measurements?.length > 0 ? data : null;
  };
  const prepareForProcessing = () => {
    hide($("#cv-buttons"));
    hide($("#cv-error"));
    hide($("#cv-actions"));
    hide($("#cv-multi-actions"));
    hide($("#cv-preview"));
    hide($("#cv-multi-results"));
    show($("#cv-loading"));
    state.set("loading", true);
  };
  const handleProcessingError = (err) => {
    hide($("#cv-loading"));
    state.set("loading", false);
    showError(err?.message || "Verbindingsfout. Controleer je internetverbinding.");
    show($("#cv-buttons"));
    if (err) console.error("CV Measure error:", err);
  };
  const processMultiFrame = async (apiUrl, file, previewDataPromise, loadingStatus) => {
    if (loadingStatus) loadingStatus.textContent = "Gevel analyseren...";
    const result = await tryMultiDetection(apiUrl, file);
    const previewData = await previewDataPromise;
    hide($("#cv-loading"));
    state.set("loading", false);
    if (result?.count > 0) {
      handleMultiDetection(result, previewData);
    } else {
      showError("Geen kozijnen gevonden. Zorg dat de gevel goed zichtbaar is.");
      show($("#cv-buttons"));
    }
  };
  const processSingleFrame = async (apiUrl, file, previewDataPromise, loadingStatus) => {
    const previewImg = $("#cv-preview-img");
    const previewData = await previewDataPromise;
    if (previewImg) previewImg.src = previewData;
    const detection = await autoDetect(apiUrl, file, (label) => {
      if (loadingStatus) loadingStatus.textContent = label;
    });
    hide($("#cv-loading"));
    state.set("loading", false);
    if (detection) {
      handleSuccessfulDetection(detection.result, detection.method);
    } else {
      showError("Kon geen kozijn detecteren. Zorg dat het kozijn goed zichtbaar is.");
      show($("#cv-buttons"));
    }
  };
  const processImage = async (file) => {
    if (!file) return;
    const loadingStatus = $("#cv-loading-status");
    prepareForProcessing();
    state.set("currentFile", file);
    const reader = new FileReader();
    const previewDataPromise = new Promise((resolve) => {
      reader.onload = (e) => {
        state.set("previewData", e.target.result);
        resolve(e.target.result);
      };
    });
    reader.readAsDataURL(file);
    try {
      const apiUrl = getApiUrl();
      if (!apiUrl) throw new Error("API niet geconfigureerd");
      if (multiFrameMode) {
        await processMultiFrame(apiUrl, file, previewDataPromise, loadingStatus);
      } else {
        await processSingleFrame(apiUrl, file, previewDataPromise, loadingStatus);
      }
    } catch (err) {
      handleProcessingError(err);
    }
  };
  const handleMultiDetection = (data, previewData) => {
    state.set("apiData", data);
    state.set("imageData", data.image || { width: 1e3, height: 1e3 });
    detectedFrames = data.measurements.map((m, i) => ({
      id: i,
      label: m.label || "Kozijn",
      confidence: m.confidence,
      box: m.box,
      dimensions: {
        width: Math.round(m.dimensions_mm.width),
        height: Math.round(m.dimensions_mm.height)
      },
      brickCount: m.brick_count,
      selected: true,
      // Select all by default
      corners: null
      // Will be set when editing
    }));
    const frameCount = $("#cv-frame-count");
    if (frameCount) frameCount.textContent = detectedFrames.length;
    const multiImg = $("#cv-multi-img");
    if (multiImg) {
      multiImg.src = previewData;
      multiImg.onload = () => {
        updateMultiOverlay();
        initMultiHandlers();
      };
    }
    updateFrameList();
    updateAddButtonState();
    show($("#cv-multi-results"));
    show($("#cv-multi-actions"));
    if (globalThis.lucide) {
      lucide.createIcons({ nodes: document.querySelectorAll("#bottomsheet-content [data-lucide]") });
    }
  };
  const updateMultiOverlay = () => {
    const svg = $("#cv-multi-overlay");
    const img = $("#cv-multi-img");
    if (!svg || !img) return;
    const imgData = state.get("imageData") || { width: 1e3, height: 1e3 };
    const scaleX = img.clientWidth / imgData.width;
    const scaleY = img.clientHeight / imgData.height;
    svg.innerHTML = "";
    detectedFrames.forEach((frame, i) => {
      const box = frame.box;
      const x = box.x1 * scaleX;
      const y = box.y1 * scaleY;
      const w = (box.x2 - box.x1) * scaleX;
      const h = (box.y2 - box.y1) * scaleY;
      const isSelected = frame.selected;
      const color = isSelected ? "#D40032" : "#666666";
      const opacity = isSelected ? 1 : 0.5;
      const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
      rect.setAttribute("x", x);
      rect.setAttribute("y", y);
      rect.setAttribute("width", w);
      rect.setAttribute("height", h);
      rect.setAttribute("fill", "none");
      rect.setAttribute("stroke", color);
      rect.setAttribute("stroke-width", isSelected ? "3" : "2");
      rect.setAttribute("opacity", opacity);
      svg.appendChild(rect);
      const labelBg = document.createElementNS("http://www.w3.org/2000/svg", "rect");
      labelBg.setAttribute("x", x);
      labelBg.setAttribute("y", y - 24);
      labelBg.setAttribute("width", Math.min(w, 120));
      labelBg.setAttribute("height", 22);
      labelBg.setAttribute("fill", color);
      labelBg.setAttribute("rx", 4);
      labelBg.setAttribute("opacity", opacity);
      svg.appendChild(labelBg);
      const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
      text.setAttribute("x", x + 6);
      text.setAttribute("y", y - 8);
      text.setAttribute("fill", "white");
      text.setAttribute("font-size", "12");
      text.setAttribute("font-weight", "bold");
      text.textContent = `#${i + 1} ${frame.dimensions.width}\xD7${frame.dimensions.height}`;
      svg.appendChild(text);
    });
  };
  const updateFrameList = () => {
    const list = $("#cv-frame-list");
    if (!list) return;
    list.innerHTML = detectedFrames.map((frame, i) => `
            <label class="flex items-center gap-3 p-3 bg-white border-2 rounded-xl cursor-pointer transition-all ${frame.selected ? "border-[#D40032] bg-red-50/30" : "border-neutral-200 hover:border-neutral-300"}">
                <input type="checkbox" class="cv-frame-checkbox w-5 h-5 accent-[#D40032]" 
                       data-frame-id="${i}" ${frame.selected ? "checked" : ""} />
                <div class="flex-1 min-w-0">
                    <div class="font-semibold text-neutral-900 flex items-center gap-2">
                        <span class="w-6 h-6 bg-[#D40032] text-white text-xs rounded-full flex items-center justify-center">
                            ${i + 1}
                        </span>
                        ${frame.label}
                    </div>
                    <div class="text-sm text-neutral-600 mt-0.5">
                        ${frame.dimensions.width} \xD7 ${frame.dimensions.height} mm
                    </div>
                </div>
                <button type="button" class="cv-frame-edit w-10 h-10 flex items-center justify-center text-neutral-400 hover:text-[#D40032] hover:bg-neutral-100 rounded-lg transition-colors" 
                        data-frame-id="${i}" title="Bewerk hoeken">
                    <i data-lucide="move" class="w-5 h-5"></i>
                </button>
            </label>
        `).join("");
    list.querySelectorAll(".cv-frame-checkbox").forEach((cb) => {
      cb.addEventListener("change", (e) => {
        const id = Number.parseInt(e.target.dataset.frameId, 10);
        detectedFrames[id].selected = e.target.checked;
        updateFrameList();
        updateMultiOverlay();
        updateAddButtonState();
      });
    });
    list.querySelectorAll(".cv-frame-edit").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const id = Number.parseInt(e.currentTarget.dataset.frameId, 10);
        editFrame(id);
      });
    });
    if (globalThis.lucide) {
      lucide.createIcons({ nodes: list.querySelectorAll("[data-lucide]") });
    }
  };
  const initMultiHandlers = () => {
    const handles = $("#cv-multi-handles");
    const img = $("#cv-multi-img");
    if (!handles || !img) return;
    const imgData = state.get("imageData") || { width: 1e3, height: 1e3 };
    const scaleX = img.clientWidth / imgData.width;
    const scaleY = img.clientHeight / imgData.height;
    handles.innerHTML = "";
    detectedFrames.forEach((frame, i) => {
      const box = frame.box;
      const div = document.createElement("div");
      div.className = "absolute cursor-pointer";
      div.style.left = `${box.x1 * scaleX}px`;
      div.style.top = `${box.y1 * scaleY}px`;
      div.style.width = `${(box.x2 - box.x1) * scaleX}px`;
      div.style.height = `${(box.y2 - box.y1) * scaleY}px`;
      div.dataset.frameId = i;
      div.addEventListener("click", () => {
        detectedFrames[i].selected = !detectedFrames[i].selected;
        updateFrameList();
        updateMultiOverlay();
        updateAddButtonState();
        if (navigator.vibrate) navigator.vibrate(10);
      });
      handles.appendChild(div);
    });
  };
  const editFrame = (frameIndex) => {
    selectedFrameIndex = frameIndex;
    const frame = detectedFrames[frameIndex];
    const imgData = state.get("imageData") || { width: 1e3, height: 1e3 };
    const box = frame.box;
    const corners = [
      { x: box.x1, y: box.y1 },
      { x: box.x2, y: box.y1 },
      { x: box.x2, y: box.y2 },
      { x: box.x1, y: box.y2 }
    ];
    frame.originalCorners = corners.map((c) => ({ ...c }));
    frame.corners = corners;
    showFrameEditor(frame, imgData);
  };
  const showFrameEditor = (frame, imgData) => {
    const preview = $("#cv-preview");
    const previewImg = $("#cv-preview-img");
    const previewData = state.get("previewData");
    if (previewImg && previewData) {
      previewImg.src = previewData;
    }
    hide($("#cv-multi-results"));
    hide($("#cv-multi-actions"));
    show(preview);
    const setupCorners = () => {
      const scaleX = previewImg.clientWidth / imgData.width;
      const scaleY = previewImg.clientHeight / imgData.height;
      const displayCorners = frame.corners.map((c) => ({
        x: c.x * scaleX,
        y: c.y * scaleY
      }));
      state.set("displayCorners", displayCorners);
      state.set("originalCorners", displayCorners.map((c) => ({ ...c })));
      updateOverlay(displayCorners);
      createCornerHandles(displayCorners);
      initDragHandlers();
      const resultDims = $("#cv-result-dims");
      if (resultDims) {
        resultDims.textContent = `${frame.dimensions.width} \xD7 ${frame.dimensions.height} mm`;
      }
      const detectionInfo = $("#cv-detection-info");
      if (detectionInfo) {
        detectionInfo.innerHTML = `
                    <i data-lucide="move" class="w-3.5 h-3.5"></i>
                    <span>Bewerk kozijn #${selectedFrameIndex + 1}</span>
                `;
        detectionInfo.className = "text-xs text-amber-400 mb-1 flex items-center gap-1";
      }
    };
    if (previewImg.complete && previewImg.naturalWidth > 0) {
      setTimeout(setupCorners, 50);
    } else {
      previewImg.addEventListener("load", () => setTimeout(setupCorners, 50), { once: true });
    }
    const actions = $("#cv-actions");
    if (actions) {
      actions.innerHTML = `
                <button type="button" id="cv-save-frame-btn" class="w-full py-4 bg-[#D40032] text-white font-bold rounded-xl hover:bg-[#b0002a] active:scale-[0.98] transition-all flex items-center justify-center gap-2 shadow-lg">
                    <i data-lucide="check" class="w-5 h-5"></i>
                    Opslaan
                </button>
                <button type="button" id="cv-cancel-frame-btn" class="w-full py-3 bg-neutral-100 text-neutral-700 font-semibold rounded-xl hover:bg-neutral-200 active:scale-[0.98] transition-all">
                    Annuleren
                </button>
            `;
      $("#cv-save-frame-btn")?.addEventListener("click", () => saveFrameEdit());
      $("#cv-cancel-frame-btn")?.addEventListener("click", () => cancelFrameEdit());
      show(actions);
    }
    if (globalThis.lucide) {
      lucide.createIcons({ nodes: document.querySelectorAll("#bottomsheet-content [data-lucide]") });
    }
  };
  const saveFrameEdit = () => {
    const frame = detectedFrames[selectedFrameIndex];
    const displayCorners = state.get("displayCorners");
    const imgData = state.get("imageData") || { width: 1e3, height: 1e3 };
    const previewImg = $("#cv-preview-img");
    if (displayCorners && previewImg) {
      const scaleX = imgData.width / previewImg.clientWidth;
      const scaleY = imgData.height / previewImg.clientHeight;
      frame.corners = displayCorners.map((c) => ({
        x: c.x * scaleX,
        y: c.y * scaleY
      }));
      const widthPx = Math.hypot(
        frame.corners[1].x - frame.corners[0].x,
        frame.corners[1].y - frame.corners[0].y
      );
      const heightPx = Math.hypot(
        frame.corners[3].x - frame.corners[0].x,
        frame.corners[3].y - frame.corners[0].y
      );
      const originalWidthPx = frame.box.x2 - frame.box.x1;
      const originalHeightPx = frame.box.y2 - frame.box.y1;
      const scaleFactorW = frame.dimensions.width / originalWidthPx;
      const scaleFactorH = frame.dimensions.height / originalHeightPx;
      frame.dimensions = {
        width: Math.round(widthPx * scaleFactorW),
        height: Math.round(heightPx * scaleFactorH)
      };
    }
    returnToMultiView();
  };
  const cancelFrameEdit = () => {
    const frame = detectedFrames[selectedFrameIndex];
    if (frame.originalCorners) {
      frame.corners = frame.originalCorners;
    }
    returnToMultiView();
  };
  const returnToMultiView = () => {
    if (dragCleanup) dragCleanup();
    hide($("#cv-preview"));
    hide($("#cv-actions"));
    const actions = $("#cv-actions");
    if (actions) {
      actions.innerHTML = `
                <button type="button" id="cv-use-btn" class="w-full py-4 bg-[#D40032] text-white font-bold rounded-xl hover:bg-[#b0002a] active:scale-[0.98] transition-all flex items-center justify-center gap-2 shadow-lg">
                    <i data-lucide="check" class="w-5 h-5"></i>
                    Gebruik deze afmetingen
                </button>
            `;
    }
    updateFrameList();
    updateMultiOverlay();
    show($("#cv-multi-results"));
    show($("#cv-multi-actions"));
    const img = $("#cv-multi-img");
    if (img?.complete) {
      initMultiHandlers();
    }
    if (globalThis.lucide) {
      lucide.createIcons({ nodes: document.querySelectorAll("#bottomsheet-content [data-lucide]") });
    }
  };
  const handleSuccessfulDetection = (data, method) => {
    const preview = $("#cv-preview");
    const previewImg = $("#cv-preview-img");
    const detectionInfo = $("#cv-detection-info");
    const resultDims = $("#cv-result-dims");
    state.set("apiData", data);
    state.set("detectionMethod", method);
    state.set("imageData", data.image || { width: 1e3, height: 1e3 });
    homographyMatrix = null;
    const refObj = data.reference_object;
    if (refObj?.corners?.length === 4) {
      const refDims = getReferenceSize(refObj.type || method, refObj);
      if (refDims) {
        homographyMatrix = computeHomography(refObj.corners, refDims.width, refDims.height);
      }
    }
    const dims = data.physical_dimensions;
    const result = {
      width: Math.round(dims.width_mm),
      height: Math.round(dims.height_mm),
      confidence: data.quality_score || null,
      method
    };
    state.set("result", result);
    if (detectionInfo) {
      const methodLabels = {
        aruco: "Marker gedetecteerd",
        a4_paper: "A4 papier gedetecteerd",
        brick_count: "Bakstenen geteld"
      };
      const iconColor = method === "brick_count" ? "text-amber-400" : "text-green-400";
      detectionInfo.className = `text-xs ${iconColor} mb-1 flex items-center gap-1`;
      detectionInfo.innerHTML = `
                <i data-lucide="${method === "brick_count" ? "grid-3x3" : "check-circle"}" class="w-3.5 h-3.5"></i>
                <span>${methodLabels[method] || "Kozijn gedetecteerd"}</span>
            `;
    }
    if (resultDims) {
      resultDims.textContent = `${result.width} \xD7 ${result.height} mm`;
    }
    const setupCorners = () => {
      const corners = data.detected_window?.corners;
      if (corners?.length === 4) {
        const imgData = data.image || { width: 1e3, height: 1e3 };
        const scaleX = previewImg.clientWidth / imgData.width;
        const scaleY = previewImg.clientHeight / imgData.height;
        const displayCorners = corners.map((c) => ({
          x: c.x * scaleX,
          y: c.y * scaleY
        }));
        state.set("displayCorners", displayCorners);
        state.set("originalCorners", displayCorners.map((c) => ({ ...c })));
        updateOverlay(displayCorners);
        createCornerHandles(displayCorners);
        initDragHandlers();
      }
    };
    if (previewImg.complete && previewImg.naturalWidth > 0) {
      setTimeout(setupCorners, 50);
    } else {
      previewImg.addEventListener("load", () => setTimeout(setupCorners, 50), { once: true });
    }
    show(preview);
    show($("#cv-actions"));
    if (globalThis.lucide) {
      lucide.createIcons({ nodes: document.querySelectorAll("#bottomsheet-content [data-lucide]") });
    }
    const hint = $("#cv-edit-hint");
    if (hint) {
      setTimeout(() => hint.style.opacity = "0", 3e3);
    }
  };
  const getReferenceSize = (type, refObj) => {
    switch (type) {
      case "aruco": {
        const size = refObj?.marker_size_mm || 100;
        return { width: size, height: size };
      }
      case "a4_paper":
        return { width: 210, height: 297 };
      default:
        return null;
    }
  };
  const updateOverlay = (corners) => {
    const outline = $("#cv-outline");
    const maskPoly = $("#cv-mask-poly");
    if (!corners || corners.length !== 4) return;
    const points = corners.map((c) => `${c.x},${c.y}`).join(" ");
    outline?.setAttribute("points", points);
    maskPoly?.setAttribute("points", points);
  };
  const createCornerHandles = (corners) => {
    const container = $("#cv-corners");
    if (!container || !corners) return;
    container.innerHTML = "";
    corners.forEach((corner, i) => {
      const handle = document.createElement("div");
      handle.className = "cv-handle absolute transition-transform duration-75";
      handle.dataset.corner = i;
      handle.style.cssText = `
                left: ${corner.x}px;
                top: ${corner.y}px;
                width: ${TOUCH_TARGET}px;
                height: ${TOUCH_TARGET}px;
                margin-left: -${TOUCH_TARGET / 2}px;
                margin-top: -${TOUCH_TARGET / 2}px;
                touch-action: none;
            `;
      handle.innerHTML = `
                <div class="cv-handle-inner absolute inset-0 m-auto w-7 h-7 bg-[#D40032] rounded-full border-[3px] border-white shadow-lg transition-transform duration-100"></div>
            `;
      container.appendChild(handle);
    });
  };
  const updateHandles = (corners) => {
    corners.forEach((corner, i) => {
      const handle = $(`#cv-corners [data-corner="${i}"]`);
      if (handle) {
        handle.style.left = `${corner.x}px`;
        handle.style.top = `${corner.y}px`;
      }
    });
  };
  const initDragHandlers = () => {
    if (dragCleanup) dragCleanup();
    const container = $("#cv-corners");
    const previewContainer = $("#cv-preview-container");
    if (!container || !previewContainer) return;
    let activeHandle = null;
    const getPointerPos = (e) => {
      const rect = previewContainer.getBoundingClientRect();
      const clientX = e.touches?.[0]?.clientX ?? e.clientX;
      const clientY = e.touches?.[0]?.clientY ?? e.clientY;
      return {
        x: Math.max(0, Math.min(rect.width, clientX - rect.left)),
        y: Math.max(0, Math.min(rect.height, clientY - rect.top))
      };
    };
    const handleStart = (e) => {
      const handle = e.target.closest(".cv-handle");
      if (!handle) return;
      e.preventDefault();
      e.stopPropagation();
      activeHandle = Number.parseInt(handle.dataset.corner, 10);
      const inner = handle.querySelector(".cv-handle-inner");
      if (inner) inner.style.transform = "scale(1.25)";
      if (navigator.vibrate) navigator.vibrate(10);
    };
    const handleMove = (e) => {
      if (activeHandle === null) return;
      e.preventDefault();
      const pos = getPointerPos(e);
      const corners = state.get("displayCorners");
      if (!corners?.[activeHandle]) return;
      const newCorners = [...corners];
      newCorners[activeHandle] = pos;
      if (isValidShape(newCorners)) {
        state.set("displayCorners", newCorners);
        updateOverlay(newCorners);
        const handle = container.querySelector(`[data-corner="${activeHandle}"]`);
        if (handle) {
          handle.style.left = `${pos.x}px`;
          handle.style.top = `${pos.y}px`;
        }
        recalculateDimensions();
      }
    };
    const handleEnd = () => {
      if (activeHandle === null) return;
      const handle = container.querySelector(`[data-corner="${activeHandle}"]`);
      if (handle) {
        const inner = handle.querySelector(".cv-handle-inner");
        if (inner) inner.style.transform = "";
      }
      if (navigator.vibrate) navigator.vibrate(15);
      activeHandle = null;
    };
    container.addEventListener("touchstart", handleStart, { passive: false });
    container.addEventListener("touchmove", handleMove, { passive: false });
    container.addEventListener("touchend", handleEnd);
    container.addEventListener("touchcancel", handleEnd);
    container.addEventListener("mousedown", handleStart);
    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleEnd);
    dragCleanup = () => {
      container.removeEventListener("touchstart", handleStart);
      container.removeEventListener("touchmove", handleMove);
      container.removeEventListener("touchend", handleEnd);
      container.removeEventListener("touchcancel", handleEnd);
      container.removeEventListener("mousedown", handleStart);
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleEnd);
      dragCleanup = null;
    };
  };
  const isValidShape = (corners) => {
    if (!corners || corners.length !== 4) return false;
    const minX = Math.min(...corners.map((c) => c.x));
    const maxX = Math.max(...corners.map((c) => c.x));
    const minY = Math.min(...corners.map((c) => c.y));
    const maxY = Math.max(...corners.map((c) => c.y));
    return maxX - minX >= MIN_SHAPE_SIZE && maxY - minY >= MIN_SHAPE_SIZE;
  };
  const recalculateDimensions = () => {
    const corners = state.get("displayCorners");
    const apiData = state.get("apiData");
    const previewImg = $("#cv-preview-img");
    const resultDims = $("#cv-result-dims");
    if (!corners || !apiData || !previewImg) return;
    const imgData = state.get("imageData") || { width: 1e3, height: 1e3 };
    const scaleX = imgData.width / previewImg.clientWidth;
    const scaleY = imgData.height / previewImg.clientHeight;
    const origCorners = corners.map((c) => ({
      x: c.x * scaleX,
      y: c.y * scaleY
    }));
    let widthMm, heightMm;
    if (homographyMatrix) {
      const realCorners = origCorners.map((c) => applyHomography(homographyMatrix, c));
      const topWidth = distance(realCorners[0], realCorners[1]);
      const bottomWidth = distance(realCorners[3], realCorners[2]);
      const leftHeight = distance(realCorners[0], realCorners[3]);
      const rightHeight = distance(realCorners[1], realCorners[2]);
      widthMm = Math.round((topWidth + bottomWidth) / 2);
      heightMm = Math.round((leftHeight + rightHeight) / 2);
    } else {
      const pxPerMm = apiData.pixels_per_mm;
      if (!pxPerMm) return;
      const topWidth = distance(origCorners[0], origCorners[1]);
      const bottomWidth = distance(origCorners[3], origCorners[2]);
      const leftHeight = distance(origCorners[0], origCorners[3]);
      const rightHeight = distance(origCorners[1], origCorners[2]);
      widthMm = Math.round((topWidth + bottomWidth) / 2 / pxPerMm);
      heightMm = Math.round((leftHeight + rightHeight) / 2 / pxPerMm);
    }
    const result = { ...state.get("result"), width: widthMm, height: heightMm };
    state.set("result", result);
    if (resultDims) {
      resultDims.textContent = `${widthMm} \xD7 ${heightMm} mm`;
    }
  };
  const distance = (p1, p2) => Math.hypot(p2.x - p1.x, p2.y - p1.y);
  const computeHomography = (refCorners, refWidthMm, refHeightMm) => {
    try {
      const src = refCorners.map((c) => ({ x: c.x, y: c.y }));
      const dst = [
        { x: 0, y: 0 },
        { x: refWidthMm, y: 0 },
        { x: refWidthMm, y: refHeightMm },
        { x: 0, y: refHeightMm }
      ];
      const A = [];
      for (let i = 0; i < 4; i++) {
        const { x: sx, y: sy } = src[i];
        const { x: dx, y: dy } = dst[i];
        A.push(
          [-sx, -sy, -1, 0, 0, 0, sx * dx, sy * dx, dx],
          [0, 0, 0, -sx, -sy, -1, sx * dy, sy * dy, dy]
        );
      }
      const h = solveHomography(A);
      return [
        [h[0], h[1], h[2]],
        [h[3], h[4], h[5]],
        [h[6], h[7], h[8]]
      ];
    } catch (e) {
      console.debug("Homography computation failed:", e.message);
      return null;
    }
  };
  const solveHomography = (A) => {
    const ATA = new Array(9).fill(null).map(() => new Array(9).fill(0));
    for (let i = 0; i < 9; i++) {
      for (let j = 0; j < 9; j++) {
        for (let k = 0; k < 8; k++) {
          ATA[i][j] += A[k][i] * A[k][j];
        }
      }
    }
    let v = new Array(9).fill(1);
    for (let iter = 0; iter < 100; iter++) {
      const Av = ATA.map((row) => row.reduce((sum, val, j) => sum + val * v[j], 0));
      const norm = Math.sqrt(Av.reduce((sum, val) => sum + val * val, 0));
      v = Av.map((val) => val / norm);
    }
    return v;
  };
  const applyHomography = (H, point) => {
    const w = H[2][0] * point.x + H[2][1] * point.y + H[2][2];
    return {
      x: (H[0][0] * point.x + H[0][1] * point.y + H[0][2]) / w,
      y: (H[1][0] * point.x + H[1][1] * point.y + H[1][2]) / w
    };
  };
  const showError = (msg) => {
    const errorEl = $("#cv-error");
    const errorMsg = $("#cv-error-msg");
    if (errorMsg) errorMsg.textContent = msg;
    show(errorEl);
    state.set("error", msg);
    if (globalThis.lucide && errorEl) {
      lucide.createIcons({ nodes: errorEl.querySelectorAll("[data-lucide]") });
    }
  };
  const reset = () => {
    state.update({
      previewData: null,
      loading: false,
      error: null,
      result: null,
      apiData: null,
      imageData: null,
      displayCorners: null,
      originalCorners: null,
      detectionMethod: null,
      currentFile: null
    });
    homographyMatrix = null;
    detectedFrames = [];
    selectedFrameIndex = 0;
    hide($("#cv-preview"));
    hide($("#cv-error"));
    hide($("#cv-actions"));
    hide($("#cv-multi-results"));
    hide($("#cv-multi-actions"));
    show($("#cv-buttons"));
    const camera = $("#cv-camera");
    const upload = $("#cv-upload");
    if (camera) camera.value = "";
    if (upload) upload.value = "";
  };
  const open = (onSuccess) => {
    multiFrameMode = false;
    Bottomsheet.open({
      title: "Meten met foto",
      content: buildContent(),
      onClose: () => state.set("error", null)
    });
    setTimeout(() => {
      initListeners(onSuccess, null);
      if (globalThis.lucide) {
        lucide.createIcons({ nodes: document.querySelectorAll("#bottomsheet-content [data-lucide]") });
      }
    }, 50);
  };
  const openMulti = (onMultiSuccess) => {
    multiFrameMode = true;
    Bottomsheet.open({
      title: "Meten met foto",
      content: buildContent(),
      onClose: () => state.set("error", null)
    });
    setTimeout(() => {
      initListeners(null, onMultiSuccess);
      setMode("multi");
      if (globalThis.lucide) {
        lucide.createIcons({ nodes: document.querySelectorAll("#bottomsheet-content [data-lucide]") });
      }
    }, 50);
  };
  return Object.freeze({
    open,
    openMulti,
    reset,
    getResult: () => state.get("result"),
    getDetectedFrames: () => detectedFrames.filter((f) => f.selected)
  });
})();
